/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo } from 'react'
import { getIn, useFormikContext } from 'formik'
import type { SelectChangeEvent } from '@mui/material'
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  OutlinedInput,
  Select,
  TextField
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import type { TargetOptionProps } from 'modules/types'
import GlobalStyle from 'modules/styles'
import {
  useGetPushNotificationCarModelDropdownMutation,
  useGetPushNotificationMemberTierDropdownMutation,
  useGetPushNotificationModelYearDropdownMutation,
  useGetPushNotificationTargetOptionDropdownMutation
} from 'store/pushNotification'
import { useGetProvinciesDropdownMutation, useGetCitiesDropdownMutation } from 'store/region'
import Icon from 'components/icon'
import {
  isValidMemberTierSelection,
  MEMBER_TIER_COMBINATION_ERROR,
  type SegmentationValues
} from './utils'

const SELECT_ALL_CITY = {
  cityId: -1,
  cityName: 'Select All Cities',
  provinceId: -1,
  provinceName: ''
}

const SegmentationFormFields = ({
  required = false,
  prefix,
  restrictMemberTierCombinations = false
}: {
  required?: boolean
  prefix?: string
  restrictMemberTierCombinations?: boolean
}) => {
  const { values, errors, touched, setFieldValue, handleBlur, setFieldTouched, validateField } =
    useFormikContext<any>()

  // Namespace helper: prepends prefix to any field path
  const f = (name: string) => (prefix ? `${prefix}.${name}` : name)

  // Read segmentation values/errors/touched relative to the prefix
  const seg: SegmentationValues = prefix ? getIn(values, prefix) ?? {} : values
  const segErrors = prefix ? getIn(errors, prefix) ?? {} : errors
  const segTouched = prefix ? getIn(touched, prefix) ?? {} : touched
  const [getMemberTierDropdown, memberTierDDL] = useGetPushNotificationMemberTierDropdownMutation()
  const [getTargetOptionDropdown, targetOptionDDL] =
    useGetPushNotificationTargetOptionDropdownMutation()

  useEffect(() => {
    getMemberTierDropdown()
    getTargetOptionDropdown()
  }, [])

  const carOwnerships = useMemo(
    () => [
      { id: 'EV', name: 'Hyundai Owner (EV)' },
      { id: 'nonEV', name: 'Hyundai Owner (Non-EV)' },
      { id: 'Otherbrand', name: 'EV Other Brand Owner' }
    ],
    []
  )

  const licensePlateTypes = useMemo(
    () => [
      { id: 'odd', name: 'Odd Plate' },
      { id: 'even', name: 'Even Plate' }
    ],
    []
  )

  const activeStatuses = useMemo(
    () => [
      { id: 'Active', name: 'Active' },
      { id: 'Inactive > 30 days', name: 'Inactive > 30 days' },
      { id: 'Inactive > 90 days', name: 'Inactive > 90 days' }
    ],
    []
  )

  const bluelinkStatuses = useMemo(
    () => [
      { id: 'bluelink-user', name: 'Bluelink User' },
      { id: 'non-bluelink-user', name: 'Non-Bluelink User' }
    ],
    []
  )

  const handleAddCarModelRow = () => {
    setFieldValue(f('CarModelYears'), [
      ...(seg.CarModelYears || []),
      { CarModelId: 0, ModelYear: [] }
    ])
  }

  const handleRemoveCarModelRow = (index: number) => {
    const newRows = [...(seg.CarModelYears || [])]
    newRows.splice(index, 1)
    setFieldValue(f('CarModelYears'), newRows)
  }

  const handleAddLocationRow = () => {
    setFieldValue(f('locations'), [
      ...(seg.locations || []),
      { provinceId: null, provinceName: '', cityId: [], cities: [] }
    ])
  }

  const handleRemoveLocationRow = (index: number) => {
    const newRows = [...(seg.locations || [])]
    newRows.splice(index, 1)
    setFieldValue(f('locations'), newRows)
  }

  const targetOptions = targetOptionDDL.data || []

  const selectedTargets = Array.isArray(seg.targetOptions) ? seg.targetOptions : []
  const isSelected = (name: string) =>
    selectedTargets.some((v) => v.toLowerCase() === name.toLowerCase())

  const handleChangeTargetOptions = (event: SelectChangeEvent<string[]>) => {
    const newValue = event.target.value as string[]
    const removed = selectedTargets.filter((v) => !newValue.includes(v))

    setFieldTouched(f('targetOptions'), true, false)
    setFieldValue(f('targetOptions'), newValue, true)

    removed.forEach((v) => {
      const opt = v.toLowerCase()
      if (opt === 'member tier target') setFieldValue(f('memberTiers'), [])
      if (opt === 'car model target') {
        setFieldValue(f('carModel'), [])
        setFieldValue(f('carModelYear'), [])
        setFieldValue(f('CarModelYears'), [])
      }
      if (opt === 'vin') setFieldValue(f('vinCsvFile'), null, true)
      if (opt === 'upload member id') setFieldValue(f('memberIdCsvFile'), null, true)
      if (opt === 'salutation') setFieldValue(f('salutation'), '', true)
      if (opt === 'location') setFieldValue(f('locations'), [], true)
      if (opt === 'license plate type') setFieldValue(f('licensePlateType'), '', true)
      if (opt === 'active status') setFieldValue(f('activeStatus'), '', true)
      if (opt === 'bluelink status') setFieldValue(f('bluelinkStatus'), '', true)
      if (opt === 'handover date') setFieldValue(f('handoverDateRange'), null, true)
    })
  }

  return (
    <>
      <FormControl fullWidth>
        <InputLabel id='targetOptions'>
          {required ? 'Target Options *' : 'Target Options'}
        </InputLabel>
        <Select
          multiple
          labelId='targetOptions'
          id='targetOptions'
          input={<OutlinedInput label={required ? 'Target Options *' : 'Target Options'} />}
          value={selectedTargets.filter((v) =>
            targetOptions.map((i: TargetOptionProps) => i.targetOption).includes(v)
          )}
          renderValue={(selected: string[]) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip key={value} label={value} size='small' />
              ))}
            </Box>
          )}
          onChange={handleChangeTargetOptions}
          error={Boolean(segTouched?.targetOptions) && Boolean(segErrors?.targetOptions)}
        >
          {targetOptions.map((item: TargetOptionProps, id: number) => (
            <MenuItem key={id} value={item.targetOption}>
              {item.targetOption}
            </MenuItem>
          ))}
        </Select>
        {segTouched?.targetOptions && segErrors?.targetOptions && (
          <FormHelperText error id='targetOptions'>
            {typeof segErrors.targetOptions === 'string' ? segErrors.targetOptions : ''}
          </FormHelperText>
        )}
      </FormControl>

      {isSelected('member tier target') && (
        <Autocomplete
          multiple
          options={
            (memberTierDDL &&
              memberTierDDL.data?.map((item) => ({
                memberId: item.id,
                memberName: item.name
              }))) ||
            []
          }
          getOptionLabel={(list) => list.memberName}
          isOptionEqualToValue={(option, value) =>
            option && value ? option.memberId == value.memberId : false
          }
          getOptionDisabled={(option) => {
            const isAlreadySelected = (seg.memberTiers ?? []).some(
              (memberTier) => memberTier.memberId === option.memberId
            )

            return (
              restrictMemberTierCombinations &&
              !isAlreadySelected &&
              !isValidMemberTierSelection([...(seg.memberTiers ?? []), option])
            )
          }}
          onOpen={() => getMemberTierDropdown()}
          onChange={async (_, data) => {
            const temp: any[] = data.map((item) => Object.assign({}, item))
            await setFieldValue(f('memberTiers'), temp)
            await setFieldTouched(f('memberTiers'), true, true)
            await validateField(f('memberTiers'))
          }}
          onBlur={handleBlur}
          ListboxProps={GlobalStyle.ListBox}
          renderOption={(props, item) => (
            <li {...props} key={item.memberId}>
              {item.memberName}
            </li>
          )}
          value={seg.memberTiers ?? []}
          renderInput={(params) => (
            <TextField
              {...params}
              name={f('memberTiers')}
              required
              label='Member Tier Target'
              error={Boolean(segTouched?.memberTiers) && Boolean(segErrors?.memberTiers)}
              helperText={
                segTouched?.memberTiers && typeof segErrors?.memberTiers === 'string'
                  ? segErrors.memberTiers
                  : restrictMemberTierCombinations
                  ? MEMBER_TIER_COMBINATION_ERROR
                  : ''
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {memberTierDDL.isLoading && <CircularProgress color='inherit' size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
      )}

      {isSelected('vin') && (
        <>
          <TextField
            type='file'
            name={f('vinCsvFile')}
            label='VIN File'
            InputLabelProps={{ shrink: true }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(f('vinCsvFile'), event.currentTarget.files?.[0])
            }}
            onBlur={handleBlur}
            error={Boolean(segTouched?.vinCsvFile) && Boolean(segErrors?.vinCsvFile)}
            helperText={segTouched?.vinCsvFile && (segErrors?.vinCsvFile as string)}
            fullWidth
            inputProps={{ accept: '.csv' }}
          />
          <Box sx={{ mt: 0.5 }}>
            <Link href='/templates/vin-upload-template.csv' download underline='always'>
              Download template (.csv format)
            </Link>
          </Box>
          {seg.vinCsvFileUrl && (
            <Button
              endIcon={<Icon icon='Download' />}
              variant='outlined'
              sx={{ width: 200 }}
              href={seg.vinCsvFileUrl}
            >
              Download CSV
            </Button>
          )}
        </>
      )}

      {isSelected('upload member id') && (
        <>
          <TextField
            type='file'
            name={f('memberIdCsvFile')}
            label='Member ID File'
            InputLabelProps={{ shrink: true }}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(f('memberIdCsvFile'), event.currentTarget.files?.[0])
            }}
            onBlur={handleBlur}
            error={Boolean(segTouched?.memberIdCsvFile) && Boolean(segErrors?.memberIdCsvFile)}
            helperText={segTouched?.memberIdCsvFile && (segErrors?.memberIdCsvFile as string)}
            fullWidth
            inputProps={{ accept: '.csv' }}
          />
          <Box sx={{ mt: 0.5 }}>
            <Link href='/templates/member-id-upload-template.csv' download underline='always'>
              Download template (.csv format)
            </Link>
          </Box>
          {seg.memberIdCsvFileUrl && (
            <Button
              endIcon={<Icon icon='Download' />}
              variant='outlined'
              sx={{ width: 200 }}
              href={seg.memberIdCsvFileUrl}
            >
              Download CSV
            </Button>
          )}
        </>
      )}

      {isSelected('car model target') && (
        <Box>
          {seg.CarModelYears?.map((carModelYear, index) => (
            <CarModelRow
              key={index}
              index={index}
              carModelId={carModelYear.CarModelId}
              modelYears={carModelYear.ModelYear || []}
              onChangeCarModel={(carModelId, carModelName) => {
                setFieldValue(f(`CarModelYears[${index}].CarModelId`), carModelId)
                setFieldValue(f(`CarModelYears[${index}].CarModelName`), carModelName)
              }}
              onChangeModelYears={(modelYears) => {
                setFieldValue(f(`CarModelYears[${index}].ModelYear`), modelYears)
              }}
              onRemove={handleRemoveCarModelRow}
              error={getIn(segErrors, `CarModelYears[${index}].ModelYear`)}
            />
          ))}
          <Button variant='outlined' onClick={handleAddCarModelRow}>
            Add Car Model
          </Button>
        </Box>
      )}

      {isSelected('salutation') && (
        <FormControl fullWidth>
          <InputLabel id='salutation-label'>Salutation</InputLabel>
          <Select
            labelId='salutation-label'
            id='salutation'
            name={f('salutation')}
            value={seg.salutation}
            label='Salutation'
            onChange={(event) => setFieldValue(f('salutation'), event.target.value)}
            onBlur={handleBlur}
            error={Boolean(segTouched?.salutation) && Boolean(segErrors?.salutation)}
          >
            <MenuItem value='Mr'>Mr.</MenuItem>
            <MenuItem value='Mrs'>Mrs.</MenuItem>
          </Select>
          {segTouched?.salutation && segErrors?.salutation && (
            <FormHelperText error>{segErrors.salutation}</FormHelperText>
          )}
        </FormControl>
      )}

      {isSelected('location') && (
        <Box>
          {seg.locations?.map((location, index) => (
            <LocationRow
              key={index}
              onChangeProvince={(provinceId, provinceName) => {
                setFieldValue(f(`locations[${index}].provinceId`), provinceId, true)
                setFieldValue(f(`locations[${index}].provinceName`), provinceName)
                setFieldValue(f(`locations[${index}].cityId`), [])
                setFieldValue(f(`locations[${index}].cities`), [])
              }}
              onBlurProvince={() => setFieldTouched(f(`locations[${index}].provinceId`), true)}
              provinceId={location.provinceId}
              cityId={location.cityId}
              onChangeCity={(cityIds, cityNames) => {
                setFieldValue(f(`locations[${index}].cityId`), cityIds, true)
                setFieldValue(f(`locations[${index}].cities`), cityNames)
              }}
              onBlurCity={() => setFieldTouched(f(`locations[${index}].cityId`), true)}
              index={index}
              onRemove={handleRemoveLocationRow}
              error={
                getIn(segTouched, `locations.${index}.cityId`) &&
                getIn(segErrors, `locations[${index}].cityId`)
              }
            />
          ))}
          <Button variant='outlined' onClick={handleAddLocationRow}>
            Add Location
          </Button>
        </Box>
      )}

      {isSelected('car ownership') && (
        <Autocomplete
          multiple
          options={carOwnerships}
          getOptionLabel={(list) => list.name}
          isOptionEqualToValue={(option, value) =>
            option && value ? option.id == value.id : false
          }
          onChange={async (_, data) => {
            setFieldValue(f('carOwnership'), data)
            await setFieldTouched(f('carOwnership'), true, true)
            await validateField(f('carOwnership'))
          }}
          onBlur={handleBlur}
          ListboxProps={GlobalStyle.ListBox}
          renderOption={(props, item) => (
            <li {...props} key={item.id}>
              {item.name}
            </li>
          )}
          value={seg.carOwnership ?? []}
          renderInput={(params) => (
            <TextField
              {...params}
              name={f('carOwnership')}
              required
              label='Vehicle Type Ownership'
              error={Boolean(segTouched?.carOwnership) && Boolean(segErrors?.carOwnership)}
              helperText={
                segTouched?.carOwnership && typeof segErrors?.carOwnership === 'string'
                  ? segErrors.carOwnership
                  : ''
              }
            />
          )}
        />
      )}

      {isSelected('license plate type') && (
        <FormControl fullWidth>
          <InputLabel id='licensePlateType-label'>License Plate Type</InputLabel>
          <Select
            labelId='licensePlateType-label'
            id='licensePlateType'
            name={f('licensePlateType')}
            value={seg.licensePlateType || ''}
            label='License Plate Type'
            onChange={(event) => setFieldValue(f('licensePlateType'), event.target.value)}
            onBlur={handleBlur}
            error={Boolean(segTouched?.licensePlateType) && Boolean(segErrors?.licensePlateType)}
            required
          >
            {licensePlateTypes.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
              </MenuItem>
            ))}
          </Select>
          {segTouched?.licensePlateType && segErrors?.licensePlateType && (
            <FormHelperText error>{segErrors.licensePlateType}</FormHelperText>
          )}
        </FormControl>
      )}

      {isSelected('active status') && (
        <FormControl fullWidth>
          <InputLabel id='activeStatus-label'>Active Status</InputLabel>
          <Select
            labelId='activeStatus-label'
            id='activeStatus'
            name={f('activeStatus')}
            value={seg.activeStatus || ''}
            label='Active Status'
            onChange={(event) => setFieldValue(f('activeStatus'), event.target.value)}
            onBlur={handleBlur}
            error={Boolean(segTouched?.activeStatus) && Boolean(segErrors?.activeStatus)}
            required
          >
            {activeStatuses.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                {status.name}
              </MenuItem>
            ))}
          </Select>
          {segTouched?.activeStatus && segErrors?.activeStatus && (
            <FormHelperText error>{segErrors.activeStatus}</FormHelperText>
          )}
        </FormControl>
      )}

      {isSelected('handover date') && (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', mb: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} dateLibInstance={dayjs}>
            <DatePicker
              value={seg.handoverDateRange?.minDate ? dayjs(seg.handoverDateRange.minDate) : null}
              onChange={(date: Dayjs | null) => {
                const val = date ? date.format('YYYY/MM/DD') : ''
                setFieldValue(f('handoverDateRange.minDate'), val)
              }}
              label='Min Handover Date'
              format='DD-MM-YYYY'
              sx={{ width: '100%' }}
              slotProps={{
                textField: {
                  name: f('handoverDateRange.minDate'),
                  onBlur: handleBlur,
                  error: Boolean(
                    getIn(segTouched, 'handoverDateRange.minDate') &&
                      getIn(segErrors, 'handoverDateRange.minDate')
                  ),
                  helperText:
                    getIn(segTouched, 'handoverDateRange.minDate') &&
                    getIn(segErrors, 'handoverDateRange.minDate'),
                  required: true,
                  fullWidth: true
                }
              }}
            />
            <DatePicker
              value={seg.handoverDateRange?.maxDate ? dayjs(seg.handoverDateRange.maxDate) : null}
              onChange={(date: Dayjs | null) => {
                const val = date ? date.format('YYYY/MM/DD') : ''
                setFieldValue(f('handoverDateRange.maxDate'), val)
              }}
              label='Max Handover Date'
              format='DD-MM-YYYY'
              sx={{ width: '100%' }}
              slotProps={{
                textField: {
                  name: f('handoverDateRange.maxDate'),
                  onBlur: handleBlur,
                  error: Boolean(
                    getIn(segTouched, 'handoverDateRange.maxDate') &&
                      getIn(segErrors, 'handoverDateRange.maxDate')
                  ),
                  helperText:
                    getIn(segTouched, 'handoverDateRange.maxDate') &&
                    getIn(segErrors, 'handoverDateRange.maxDate'),
                  required: true,
                  fullWidth: true
                }
              }}
            />
          </LocalizationProvider>
        </Box>
      )}

      {isSelected('bluelink status') && (
        <FormControl fullWidth>
          <InputLabel id='bluelinkStatus-label'>Bluelink Status</InputLabel>
          <Select
            labelId='bluelinkStatus-label'
            id='bluelinkStatus'
            name={f('bluelinkStatus')}
            value={seg.bluelinkStatus || ''}
            label='Bluelink Status'
            onChange={(event) => setFieldValue(f('bluelinkStatus'), event.target.value)}
            onBlur={handleBlur}
            error={Boolean(segTouched?.bluelinkStatus) && Boolean(segErrors?.bluelinkStatus)}
            required
          >
            {bluelinkStatuses.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                {status.name}
              </MenuItem>
            ))}
          </Select>
          {segTouched?.bluelinkStatus && segErrors?.bluelinkStatus && (
            <FormHelperText error>{segErrors.bluelinkStatus}</FormHelperText>
          )}
        </FormControl>
      )}
    </>
  )
}

export default SegmentationFormFields

const CarModelRow = ({
  index,
  carModelId,
  modelYears = [],
  onChangeCarModel,
  onChangeModelYears,
  onRemove,
  error
}: {
  index: number
  carModelId: number
  modelYears: number[]
  onChangeCarModel: (carModelId: number, carModelName: string) => void
  onChangeModelYears: (modelYears: number[]) => void
  onRemove: (index: number) => void
  error?: string
}) => {
  const [getCarModelDropdown, carModelDDL] = useGetPushNotificationCarModelDropdownMutation()
  const [getModelYearDropdown, modelYearDDL] = useGetPushNotificationModelYearDropdownMutation()

  useEffect(() => {
    getCarModelDropdown()
    getModelYearDropdown()
  }, [])

  const [carModelsData, yearsData] = useMemo(() => {
    const cars = (carModelDDL.data || []).map((item) => item)
    const years = (modelYearDDL.data || []).map((item) => item.year)
    return [cars, years]
  }, [carModelDDL, modelYearDDL])

  return (
    <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
      <FormControl fullWidth>
        <InputLabel>Car Model</InputLabel>
        <Select
          value={carModelId || ''}
          onChange={(e) => {
            const selectedCarModelId = Number(e.target.value)
            const carModelName = carModelsData
              .filter((m) => m.id === selectedCarModelId)
              .map((m) => m.name)
              .join()
            onChangeCarModel(selectedCarModelId, carModelName)
          }}
          label='Car Model'
        >
          <MenuItem value=''>
            <em>Select Car Model</em>
          </MenuItem>
          {carModelsData.map((model) => (
            <MenuItem key={model.id} value={model.id}>
              {model.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Autocomplete
        multiple
        fullWidth
        options={yearsData}
        getOptionLabel={(option) => option?.toString() || `${option}`}
        isOptionEqualToValue={(option, value) => option === value}
        value={modelYears}
        onChange={(_, newValue) => {
          onChangeModelYears(newValue)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Model Year'
            placeholder='Select model years'
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {modelYearDDL.isLoading && <CircularProgress color='inherit' size={20} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option}>
            {option}
          </li>
        )}
      />
      <IconButton color='primary' onClick={() => onRemove(index)}>
        <DeleteIcon />
      </IconButton>
    </Box>
  )
}

const LocationRow = ({
  index,
  provinceId,
  cityId = [],
  onChangeProvince,
  onChangeCity,
  onRemove,
  onBlurProvince,
  onBlurCity,
  error
}: {
  index: any
  provinceId: number | null
  cityId: number[]
  onChangeProvince: (id: number, name: string) => void
  onChangeCity: (cityIds: number[], cityNames: string[]) => void
  onBlurCity: any
  onBlurProvince: any
  onRemove: (index: number) => void
  error?: string
}) => {
  const [getProvinceDropdown, provinceDDL] = useGetProvinciesDropdownMutation()
  const [getCityDropdown, cityDDL] = useGetCitiesDropdownMutation()

  useEffect(() => {
    if (!provinceDDL.data) {
      getProvinceDropdown()
    }

    if (provinceId) {
      getCityDropdown([provinceId])
    }
  }, [provinceId])

  const [provincesData, citiesData] = useMemo(() => {
    const provinces = (provinceDDL.data || []).map((item) => item)
    const cities = (cityDDL.data || []).map((item) => item)
    return [provinces, cities]
  }, [provinceDDL, cityDDL])

  return (
    <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
      <FormControl fullWidth>
        <InputLabel>Province</InputLabel>
        <Select
          value={provinceId || ''}
          onChange={(e) => {
            const selectedProvinceId = Number(e.target.value)
            const selectedProvince = provincesData.find((p) => p.provinceId === selectedProvinceId)
            onChangeProvince(selectedProvinceId, selectedProvince?.provinceName || '')
          }}
          label='Province'
          onBlur={onBlurProvince}
        >
          <MenuItem value=''>
            <em>All Provinces</em>
          </MenuItem>
          {provincesData.map((province) => (
            <MenuItem key={province.provinceId} value={province.provinceId}>
              {province.provinceName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Autocomplete
        multiple
        fullWidth
        disabled={!provinceId}
        options={citiesData.length ? [SELECT_ALL_CITY, ...citiesData] : citiesData}
        getOptionLabel={(option) => option?.cityName || ''}
        isOptionEqualToValue={(option, value) => option?.cityId === value?.cityId}
        value={citiesData.filter((city) => cityId.includes(city.cityId))}
        onBlur={onBlurCity}
        onChange={(_, newValue) => {
          const selectedAll = newValue.some((city) => city?.cityId === SELECT_ALL_CITY.cityId)
          const cities = selectedAll
            ? citiesData
            : newValue.filter((city) => city?.cityId !== SELECT_ALL_CITY.cityId)
          const cityIds = cities.map((city) => city?.cityId).filter((id) => id !== undefined)
          const cityNames = cities
            .map((city) => city?.cityName)
            .filter((name) => name !== undefined)
          onChangeCity(cityIds, cityNames)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Cities'
            placeholder='Select cities'
            error={!!error}
            helperText={error}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {cityDDL.isLoading && <CircularProgress color='inherit' size={20} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option?.cityId}>
            {option?.cityId === SELECT_ALL_CITY.cityId ? (
              <strong>{option.cityName}</strong>
            ) : (
              option?.cityName
            )}
          </li>
        )}
      />
      <IconButton color='primary' onClick={() => onRemove(index)}>
        <DeleteIcon />
      </IconButton>
    </Box>
  )
}
